import { useAppDispatch } from '@/app/hooks';
import SusDataTable from '@/components/Sus/SusDataTable';
import SusSearch from '@/components/Sus/SusSearch';
import SusDeleteSelectedButton from '@/components/Sus/buttons/SusDeleteSelectedButton';
import { openSusFormDrawer } from '@/features/drawer/drawerSlice';
import type { SUSRow } from '@/models/sus.type';
import { useIsAdmin } from '@/shared/auth/useDirectusUser';
import { useUserEtablissements } from '@/shared/auth/useUserEtablissements';
import { Button, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { useDocumentTitle } from '@mantine/hooks';
import { IconPlus } from '@tabler/icons-react';
import { useTranslate } from '@tolgee/react';
import { useState } from 'react';

/**
 * Services d'urgence : les administrateurs voient et gèrent tous les services, les autres
 * utilisateurs (référents RPU) consultent uniquement ceux de leurs établissements.
 */
const SusPage = () => {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const isAdmin = useIsAdmin();
  const { finess, isLoading } = useUserEtablissements();
  const [selectedRecords, setSelectedRecords] = useState<SUSRow[]>([]);
  const [search, setSearch] = useState('');

  useDocumentTitle(t('admin.sus.title'));

  const tableProps = { search, selectedRecords, onSelectedRecordsChange: setSelectedRecords };
  const showSearch = isAdmin || finess.length > 0;

  // La sélection ne survit pas à une nouvelle recherche : pas de lignes sélectionnées invisibles.
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setSelectedRecords([]);
  };

  const renderContent = () => {
    if (isAdmin) return <SusDataTable canEdit {...tableProps} />;
    if (isLoading) return <Loader size="sm" />;
    if (finess.length === 0) return <Text role="status">{t('sus.noEtablissement')}</Text>;
    return <SusDataTable canEdit={false} {...tableProps} />;
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center" gap="sm">
        <Title order={1}>{t('admin.sus.title')}</Title>
        <Group gap="sm">
          {showSearch && <SusSearch onSearch={handleSearchChange} />}
          {isAdmin && selectedRecords.length > 0 && (
            <SusDeleteSelectedButton
              selectedIds={selectedRecords.map(({ id }) => id)}
              onDeleted={() => setSelectedRecords([])}
            />
          )}
          {isAdmin && (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => dispatch(openSusFormDrawer(t('admin.sus.add')))}
            >
              {t('admin.sus.add')}
            </Button>
          )}
        </Group>
      </Group>
      {renderContent()}
    </Stack>
  );
};

export default SusPage;
